import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SolEstimateTableSellAsGroup } from '../../../models/sol-estimate-table/SolEstimateTableSellAsGroup';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SolEstimatePricingMode } from '../../../models/enum';
import { MatInputModule } from '@angular/material/input';
import { DecimalPrecisionPipe } from "../../../pipes/decimal-precision-pipe/decimal-precision.pipe";
import { MultilingualCurrencyPipe } from "../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe";
import { CommonDataService } from '../../../services/common-data.service';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { NumericMaskDirective } from '../../../shared/numeric/numeric-mask.directive';
import { MatTooltip } from '@angular/material/tooltip';
import { SellAsMinimumMarginService } from '../../../services/sell-as-minimum-margin.service';
import { CONSTANTS } from '../../../Constants/Constants';
import { SolEstimateService } from '../../../services/estimate/sol-estimate.service';
import { SolEstimateTableCommonFunctionsService } from '../../../services/sol-estimate-table-common-functions/sol-estimate-table-common-functions.service';

@Component({
  selector: 'app-sol-estimate-table-sell-as',
  imports: [
    CommonModule,
    TranslatePipe,
    MatFormFieldModule,
    MatInputModule,
    NumericMaskDirective,
    MatButtonToggleModule,
    ReactiveFormsModule,
    DecimalPrecisionPipe,
    MultilingualCurrencyPipe,
    MatTooltip
],
  templateUrl: './sol-estimate-table-sell-as.component.html',
  styleUrl: './sol-estimate-table-sell-as.component.scss'
})
export class SolEstimateTableSellAsComponent implements OnInit, OnChanges, OnDestroy {

  @Input() isCollaborativeNotEdit: boolean = false;
  @Input() isMarkUpSelected: boolean = false;
  @Input() sellAsFormgroup: FormGroup<SolEstimateTableSellAsGroup>;

  solEstimatePricingMode = SolEstimatePricingMode;
  get isFixedPriceDisabled(): boolean {
    return this.getCurrentSellAsSellPrice() === CONSTANTS.ZERO;
  }


  showMinimumMarginWarningIcon = false;
  minimumMarginTooltipMessage = CONSTANTS.EMPTY_STRING;

  private readonly destroy$ = new Subject<void>();
  private isSellAsSellPriceSubscriptionInitialized = false;
  private isReportedMarginSubscriptionInitialized = false;
  private minimumMarginGmValue = CONSTANTS.ZERO;

  constructor(private readonly commonDataService: CommonDataService,
    private readonly solEstimateService: SolEstimateService,
    private readonly sellAsMinimumMarginService: SellAsMinimumMarginService,
    private readonly translate: TranslateService,
    public readonly solEstimateTableCommonFunctionsService: SolEstimateTableCommonFunctionsService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isMarkUpSelected']) {
      this.refreshMinimumMarginWarningState();
    }
  }

  ngOnInit() {
    this.setupEstimateOpenSubscription();
    this.setupSellAsSellPriceUpdatedSubscription();
    this.initializeMinimumMarginState();
  }

  private setupEstimateOpenSubscription(): void {
    this.commonDataService.estimateDataChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe((estimateData) => {
        if (!estimateData || this.isDefaultEstimate()) {
          return;
        }

        this.refreshMinimumMarginForCurrentSellPrice();
      });
  }

  private setupSellAsSellPriceUpdatedSubscription(): void {
    this.solEstimateService.onSellAsSellPriceUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((sellPrice) => {
        if (this.isDefaultEstimate()) {
          return;
        }

        this.loadMinimumMarginTooltipMessage(this.sellAsMinimumMarginService.toNumberOrNull(sellPrice));
        this.ensureReactiveSubscriptions();
      });
  }

  private initializeMinimumMarginState(): void {
    if (this.isDefaultEstimate()) {
      return;
    }

    this.refreshMinimumMarginForCurrentSellPrice();
  }

  private refreshMinimumMarginForCurrentSellPrice(): void {
    this.loadMinimumMarginTooltipMessage(this.getCurrentSellAsSellPrice());
    this.ensureReactiveSubscriptions();
  }

  private ensureReactiveSubscriptions(): void {
    this.ensureSellAsSellPriceSubscription();
    this.ensureReportedMarginSubscription();
  }

  private isDefaultEstimate(): boolean {
    return this.commonDataService.getEstimateId() === CONSTANTS.DEFAULT_ESTIMATE_ID;
  }

  private ensureSellAsSellPriceSubscription(): void {
    if (this.isSellAsSellPriceSubscriptionInitialized || !this.sellAsFormgroup?.controls?.sellAsSellPrice) {
      return;
    }
    this.isSellAsSellPriceSubscriptionInitialized = true;
    this.subscribeToSellAsSellPriceChanges();
  }

  private ensureReportedMarginSubscription(): void {
    if (this.isReportedMarginSubscriptionInitialized) {
      return;
    }

    this.isReportedMarginSubscriptionInitialized = true;
    this.subscribeToReportedMarginChanges();
  }

  private subscribeToSellAsSellPriceChanges(): void {
    const sellAsSellPriceControl = this.sellAsFormgroup?.controls?.sellAsSellPrice;
    if (!sellAsSellPriceControl) {
      return;
    }

    sellAsSellPriceControl.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(250), distinctUntilChanged())
      .subscribe((sellPrice: number | string | null) => {
        this.loadMinimumMarginTooltipMessage(this.sellAsMinimumMarginService.toNumberOrNull(sellPrice));
      });
  }

  private subscribeToReportedMarginChanges(): void {
    this.sellAsFormgroup?.controls.reported?.controls.grossMarginPercentage?.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(250), distinctUntilChanged())
      .subscribe(() => {
        this.refreshMinimumMarginWarningState();
      });
  }

  private async loadMinimumMarginTooltipMessage(sellAsSellPrice: number | null): Promise<void> {
    try {
      const minimumMarginGm = await this.sellAsMinimumMarginService.getSellAsMinimumMarginForSellPrice(sellAsSellPrice);
      this.minimumMarginGmValue = minimumMarginGm;
      this.refreshMinimumMarginWarningState();
    } catch {
      this.minimumMarginTooltipMessage = CONSTANTS.EMPTY_STRING;
      this.showMinimumMarginWarningIcon = false;
    }
  }

  private refreshMinimumMarginWarningState(): void {
    const precision = this.getPrecision();
    const minimumMargin = this.minimumMarginGmValue;
    const reportedMargin = this.sellAsMinimumMarginService.toNumberOrNull(
      this.sellAsFormgroup?.controls.reported?.controls.grossMarginPercentage?.value
    );

    const tooltipKey = this.sellAsMinimumMarginService.getWarningTooltipKey(this.isMarkUpSelected);
    const tooltipValue = this.sellAsMinimumMarginService.getWarningTooltipValue(
      minimumMargin,
      this.isMarkUpSelected,
      precision
    );
    this.minimumMarginTooltipMessage = this.translate.instant(tooltipKey, { value: tooltipValue });

    this.showMinimumMarginWarningIcon =
      this.sellAsMinimumMarginService.shouldShowMinimumMarginWarning(reportedMargin, minimumMargin);
  }

  private getPrecision(): number {
    return this.commonDataService.getDecimalPrecision() ?? CONSTANTS.TWO;
  }

  private getCurrentSellAsSellPrice(): number | null {
    const formSellPrice = this.sellAsMinimumMarginService.toNumberOrNull(this.sellAsFormgroup?.controls.sellAsSellPrice?.value);
    if (formSellPrice != null) {
      return formSellPrice;
    }

    const metadataSellPrice = this.commonDataService.getEstimateMetaData()?.estimateTab?.soldASSellPrice;
    return this.sellAsMinimumMarginService.toNumberOrNull(metadataSellPrice);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
